import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MOODS } from '../../utils/constants';

const MoodPicker = ({ selectedMood, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customMood, setCustomMood] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleMoodSelect = (mood) => {
    onChange(mood.emoji);
    setIsOpen(false);
  };

  const handleCustomMoodSubmit = () => {
    if (customMood.trim()) {
      onChange(customMood.trim());
      setCustomMood('');
      setShowCustomInput(false);
      setIsOpen(false);
    }
  };

  const selectedMoodData = MOODS.find(mood => mood.emoji === selectedMood);

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Mood
      </label>
      
      {/* Selected Mood Display */}
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full input-field flex items-center justify-between"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center space-x-3">
          {selectedMood ? (
            <>
              <span className="text-2xl">{selectedMood}</span>
              <span className="text-gray-700">
                {selectedMoodData?.label || 'Custom mood'}
              </span>
            </>
          ) : (
            <span className="text-gray-500">Select your mood</span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          ▼
        </motion.div>
      </motion.button>

      {/* Mood Picker Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-4"
          >
            {/* Preset Moods */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {MOODS.map((mood, index) => (
                <motion.button
                  key={mood.emoji}
                  type="button"
                  onClick={() => handleMoodSelect(mood)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                    selectedMood === mood.emoji
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">{mood.emoji}</div>
                    <div className={`text-xs font-medium ${mood.color}`}>
                      {mood.label}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Custom Mood Section */}
            <div className="border-t border-gray-200 pt-4">
              {!showCustomInput ? (
                <motion.button
                  type="button"
                  onClick={() => setShowCustomInput(true)}
                  className="w-full p-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  + Add custom mood
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-3"
                >
                  <input
                    type="text"
                    value={customMood}
                    onChange={(e) => setCustomMood(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCustomMoodSubmit()}
                    placeholder="Enter custom mood (emoji or text)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <motion.button
                      type="button"
                      onClick={handleCustomMoodSubmit}
                      className="flex-1 bg-primary-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-primary-700 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Add
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomMood('');
                      }}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MoodPicker
